import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'react'
import { AgreementSelectors } from '../store/agreement/selectors'
import { AgreementActions } from '../store/agreement'
import { Agreement, AgreementStatusAccept, AgreementStatusConfirm, AgreementStatusReject } from '../types/agreement'
import { SessionSelectors } from '../store/session/selectors'
import { CompanySelectors } from '../store/company/selectors'
import { CompanyActions } from '../store/company'

export default function useAgreements(args?: { fetch?: boolean }) {
  const agreements = useSelector(AgreementSelectors.agreements)
  const settings = useSelector(CompanySelectors.agreementSettings)
  const companyId = useSelector(SessionSelectors.companyId)
  const dispatch = useDispatch()

  useEffect(() => {
    if (args?.fetch === false) {
      return
    }
    dispatch(AgreementActions.fetchAgreements())
    dispatch(CompanyActions.fetchAgreementSettings())
  }, [dispatch, args])

  return {
    settings,
    getCompanyAgreements: (keyword: string | undefined = undefined) => {
      const companyAgreements: Agreement[] = []
      agreements.forEach((agreement) => {
        if (agreement.status !== AgreementStatusConfirm) {
          return
        }
        const company = agreement.companies.find((agreementCompany) => agreementCompany.company.id === companyId)
        const client = agreement.companies.find((agreementCompany) => agreementCompany.company.id !== companyId)
        if (company && company.confirmable && company.confirmedAt === null) {
          if (keyword) {
            if (agreement.name.includes(keyword)) {
              companyAgreements.push(agreement)
            } else if (client && client.company.name.includes(keyword)) {
              companyAgreements.push(agreement)
            }
          } else {
            companyAgreements.push(agreement)
          }
        }
      })
      return companyAgreements
    },
    getUnconfirmCompanyAgreements: (userId: string) => {
      // 未確認の契約書リストを返す
      const companyAgreements: Agreement[] = []
      agreements.forEach((agreement) => {
        if (agreement.status !== AgreementStatusConfirm) {
          return
        }
        const companyFlows = agreement.companyFlows.find((flows) => flows.companyId === companyId)
        if (companyFlows) {
          if (companyFlows.flows.length === 0) {
            const company = agreement.companies.find((agreementCompany) => agreementCompany.company.id === companyId)
            if (company && company.confirmable && company.confirmedAt === null) {
              companyAgreements.push(agreement)
            }
          } else {
            companyFlows.flows.forEach((flow) => {
              if (flow.user.id === userId && flow.status === AgreementStatusConfirm) {
                companyAgreements.push(agreement)
              }
            })
          }
        }
      })
      return companyAgreements
    },
    getClientAgreements: (keyword: string | undefined = undefined) => {
      const clientAgreements: Agreement[] = []
      agreements.forEach((agreement) => {
        if (agreement.status !== AgreementStatusConfirm) {
          return
        }
        const client = agreement.companies.find((agreementCompany) => agreementCompany.company.id !== companyId)
        if (client && client.confirmable && client.confirmedAt === null) {
          if (keyword) {
            if (agreement.name.includes(keyword)) {
              clientAgreements.push(agreement)
            } else if (client.company.name.includes(keyword)) {
              clientAgreements.push(agreement)
            }
          } else {
            clientAgreements.push(agreement)
          }
        }
      })
      return clientAgreements
    },
    getAcceptedAgreements: (keyword: string | undefined = undefined) =>
      agreements.filter((agreement) => {
        if (agreement.status !== AgreementStatusAccept) {
          return false
        }
        const client = agreement.companies.find((agreementCompany) => agreementCompany.company.id !== companyId)
        if (keyword) {
          if (agreement.name.includes(keyword)) {
            return true
          }
          return !!(client && client.company.name.includes(keyword))
        }
        return true
      }),
    getRejectAgreements: (keyword: string | undefined = undefined) =>
      agreements.filter((agreement) => {
        if (agreement.status !== AgreementStatusReject) {
          return false
        }
        const client = agreement.companies.find((agreementCompany) => agreementCompany.company.id !== companyId)
        if (keyword) {
          if (agreement.name.includes(keyword)) {
            return true
          }
          return !!(client && client.company.name.includes(keyword))
        }
        return true
      }),
    findFlows: (agreement: Agreement) => {
      const company = agreement.companies.find((agreementCompany) => agreementCompany.company.id === companyId)
      if (!company || company.confirmedAt !== null) {
        return null
      }
      const companyFlows = agreement.companyFlows.find((flows) => flows.companyId === companyId)
      if (!companyFlows || companyFlows.flows.length === 0) {
        return null
      }
      return companyFlows.flows
    },
    findCurrentFlow: (agreement: Agreement) => {
      const company = agreement.companies.find((agreementCompany) => agreementCompany.company.id === companyId)
      if (!company || company.confirmedAt !== null) {
        return null
      }
      const companyFlows = agreement.companyFlows.find((flows) => flows.companyId === companyId)
      if (!companyFlows || companyFlows.flows.length === 0) {
        return null
      }
      const currentFlow = companyFlows.flows.find((flow) => flow.status === AgreementStatusConfirm)
      return currentFlow ?? null
    },
  }
}
